import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTheme } from '../components/ThemeContext';
import { useGlobalFilters } from '../components/GlobalFilterContext';
import { API_BASE } from '../../config';

const PATTERN_LABELS: Record<string, string> = {
  structural_rise_china: "Structural Rise \u2014 China",
  china_india_substitution: "China\u2192India Substitution",
  reacceleration_after_decline: "Reacceleration After Decline",
  concentration_risk: "Concentration Risk",
  temporary_spike: "Temporary Spike",
  stable_low_dependency: "Stable \u2014 Low Dependency",
  stable_high_dependency: "Stable \u2014 High Dependency",
  volatility_no_trend: "High Volatility \u2014 No Trend",
  india_dominant: "India Dominant",
  diversified: "Diversified",
  insufficient_data: "Insufficient Data",
};

const REPORTERS: Array<{ code: string; name: string }> = [
  { code: "842", name: "United States" },
  { code: "251", name: "France" },
  { code: "276", name: "Germany" },
  { code: "826", name: "United Kingdom" },
  { code: "380", name: "Italy" },
  { code: "724", name: "Spain" },
  { code: "392", name: "Japan" },
  { code: "410", name: "Rep. of Korea" },
  { code: "124", name: "Canada" },
  { code: "036", name: "Australia" },
  { code: "076", name: "Brazil" },
  { code: "484", name: "Mexico" },
  { code: "528", name: "Netherlands" },
  { code: "056", name: "Belgium" },
  { code: "040", name: "Austria" },
  { code: "616", name: "Poland" },
  { code: "752", name: "Sweden" },
  { code: "757", name: "Switzerland" },
  { code: "376", name: "Israel" },
  { code: "702", name: "Singapore" },
  { code: "764", name: "Thailand" },
  { code: "792", name: "T\u00fcrkiye" },
  { code: "784", name: "United Arab Emirates" },
  { code: "032", name: "Argentina" },
  { code: "344", name: "Hong Kong SAR" },
  { code: "446", name: "Macao SAR" },
  { code: "699", name: "India" },
];

/* Well-known partner codes for consistent colors */
const COUNTRY_COLORS: Record<string, string> = {
  '156': '#DC2626', // China - red
  '699': '#D97706', // India - orange
  '842': '#3B82F6', // United States - blue
  '276': '#059669', // Germany - green
  '757': '#7C3AED', // Switzerland - purple
  '372': '#0EA5E9', // Ireland - sky
  '826': '#6366F1', // United Kingdom - indigo
  '392': '#EC4899', // Japan - pink
  '702': '#14B8A6', // Singapore - teal
  '528': '#F97316', // Netherlands - deep orange
};
const DEFAULT_COUNTRY_COLOR = '#6B7280';

/* Major countries that get priority ordering */
const MAJOR_PARTNER_CODES = ['156', '699', '842'];

interface CountryBreakdownEntry {
  partner_code: string;
  name: string;
  pct: number;
}

interface RiskRow {
  hs4_code: string;
  hs4_name: string | null;
  reporter_code: string;
  china_pct: number;
  india_pct: number;
  us_pct: number;
  risk_tier: string;
  pattern_type: string;
  china_yoy_latest: number;
  china_trend_precovid: number;
  year_min: number;
  year_max: number;
  acceleration_flag: boolean;
  displacement_flag: boolean;
  narrative_summary: string | null;
  narrative_short_term: string | null;
  narrative_long_term: string | null;
  narrative_full: string | null;
  narrative_needs_refresh: boolean;
  series_china: Array<{ year: number; pct: number }>;
  country_breakdown: CountryBreakdownEntry[];
}

interface StatSummary {
  CRITICAL: number;
  HIGH: number;
  MEDIUM: number;
  LOW: number;
}

interface ApiResponse {
  rows: RiskRow[];
  stat_summary: StatSummary;
  total: number;
}

/** A country section: partner_code + name + color + the rows grouped to it */
interface CountrySection {
  partnerCode: string;
  name: string;
  color: string;
  rows: RiskRow[];
}

interface ApiIntelligencePageProps {
  SectionHeader: React.ComponentType<{ children: React.ReactNode; sub?: string }>;
}

export default function ApiIntelligencePage({ SectionHeader }: ApiIntelligencePageProps) {
  const { T } = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const { filters: globalFilters } = useGlobalFilters();

  const [reporterCode, setReporterCode] = useState(searchParams.get('reporter') || '842');

  // Sync reporter code from global country filter
  useEffect(() => {
    if (globalFilters.country.length > 0) {
      const countryName = globalFilters.country[0];
      const match = REPORTERS.find(r => r.name === countryName);
      if (match) {
        setReporterCode(match.code);
      }
    }
  }, [globalFilters.country]);

  const [hsChapter, setHsChapter] = useState<string>(searchParams.get('hs_chapter') || '');
  const [riskTierFilter, setRiskTierFilter] = useState<string>(searchParams.get('risk_tier') || '');
  const [exposureFilter, setExposureFilter] = useState<string>(searchParams.get('exposure') || 'all');
  const [data, setData] = useState<ApiResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedRow, setSelectedRow] = useState<RiskRow | null>(null);
  const [drawerTab, setDrawerTab] = useState<'summary' | 'short_term' | 'long_term' | 'full'>('summary');
  const [sortField, setSortField] = useState<string>('dominant_pct');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  // Collapsible sections keyed by partner_code
  // Capture the initial expand param in a ref so setSearchParams can't clear it
  // before countrySections are computed from the async data fetch.
  const initialExpandRef = useRef<string | null>(searchParams.get('expand'));
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
  const [initialExpandDone, setInitialExpandDone] = useState(false);

  const reporterName = REPORTERS.find(r => r.code === reporterCode)?.name || 'Unknown';

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ reporter_code: reporterCode, limit: '500' });
      if (hsChapter) params.set('hs_chapter', hsChapter);
      if (riskTierFilter) params.set('risk_tier', riskTierFilter);
      const resp = await fetch(`${API_BASE}/metasupply/api-intelligence?${params}`, { credentials: 'include' });
      if (resp.ok) {
        const d = await resp.json();
        setData(d);
      }
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [reporterCode, hsChapter, riskTierFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const params: Record<string, string> = { reporter: reporterCode };
    if (hsChapter) params.hs_chapter = hsChapter;
    if (riskTierFilter) params.risk_tier = riskTierFilter;
    if (exposureFilter !== 'all') params.exposure = exposureFilter;
    setSearchParams(params, { replace: true });
  }, [reporterCode, hsChapter, riskTierFilter, exposureFilter, setSearchParams]);

  const tierColor = (tier: string) => {
    switch (tier) {
      case 'CRITICAL': return '#DC2626';
      case 'HIGH': return '#DC2626';
      case 'MEDIUM': return '#D97706';
      case 'LOW': return '#059669';
      default: return T.textLight;
    }
  };

  const tierBg = (tier: string) => {
    switch (tier) {
      case 'CRITICAL': return '#FEF2F2';
      case 'HIGH': return '#FEF2F2';
      case 'MEDIUM': return '#FFFBEB';
      case 'LOW': return '#ECFDF5';
      default: return T.offWhite;
    }
  };

  const tierBorder = (tier: string) => {
    switch (tier) {
      case 'CRITICAL': return '#FECACA';
      case 'HIGH': return '#FECACA';
      case 'MEDIUM': return '#FDE68A';
      case 'LOW': return '#BBF7D0';
      default: return T.border;
    }
  };

  /* -- Group rows by dominant source country -- */
  const countrySections: CountrySection[] = useMemo(() => {
    const allRows = data?.rows || [];
    if (allRows.length === 0) return [];

    const grouped: Record<string, { name: string; color: string; rows: RiskRow[] }> = {};

    for (const row of allRows) {
      const breakdown = row.country_breakdown || [];
      let dominant: CountryBreakdownEntry | null = breakdown.length > 0 ? breakdown[0] : null;
      if (!dominant) {
        // Fallback to pre-computed fields
        if (row.china_pct >= row.india_pct && row.china_pct >= (row.us_pct || 0)) {
          dominant = { partner_code: '156', name: 'China', pct: row.china_pct };
        } else if (row.india_pct >= row.china_pct && row.india_pct >= (row.us_pct || 0)) {
          dominant = { partner_code: '699', name: 'India', pct: row.india_pct };
        } else {
          dominant = { partner_code: '842', name: 'United States', pct: row.us_pct || 0 };
        }
      }

      const code = dominant.partner_code;
      if (!grouped[code]) {
        grouped[code] = {
          name: dominant.name,
          color: COUNTRY_COLORS[code] || DEFAULT_COUNTRY_COLOR,
          rows: [],
        };
      }
      grouped[code].rows.push(row);
    }

    // Sort: major countries first (China, India, US), then by row count descending
    const entries = Object.entries(grouped);
    entries.sort((a, b) => {
      const aMajor = MAJOR_PARTNER_CODES.indexOf(a[0]);
      const bMajor = MAJOR_PARTNER_CODES.indexOf(b[0]);
      if (aMajor !== -1 && bMajor !== -1) return aMajor - bMajor;
      if (aMajor !== -1) return -1;
      if (bMajor !== -1) return 1;
      return b[1].rows.length - a[1].rows.length;
    });

    return entries.map(([code, val]) => ({
      partnerCode: code,
      name: val.name,
      color: val.color,
      rows: val.rows,
    }));
  }, [data]);

  // Set initial expanded state after sections are computed
  useEffect(() => {
    if (countrySections.length === 0 || initialExpandDone) return;

    const ep = initialExpandRef.current;
    const newExpanded: Record<string, boolean> = {};
    if (ep === 'other') {
      // Expand all non-major sections, collapse major ones
      for (const s of countrySections) {
        newExpanded[s.partnerCode] = !MAJOR_PARTNER_CODES.includes(s.partnerCode);
      }
    } else if (ep) {
      // ep could be a keyword like 'china', 'india', 'us' or a partner_code
      const codeMap: Record<string, string> = { china: '156', india: '699', us: '842' };
      const targetCode = codeMap[ep] || ep;
      for (const s of countrySections) {
        newExpanded[s.partnerCode] = s.partnerCode === targetCode;
      }
    } else {
      // Default: expand first section only (usually China)
      for (let i = 0; i < countrySections.length; i++) {
        newExpanded[countrySections[i].partnerCode] = i === 0;
      }
    }
    setExpandedSections(newExpanded);
    setInitialExpandDone(true);
  }, [countrySections, initialExpandDone]);

  // Build exposure dropdown options from discovered country sections
  const exposureCountryOptions = useMemo(() => {
    const opts: Array<{ value: string; label: string }> = [{ value: 'all', label: 'All Countries' }];
    for (const s of countrySections) {
      opts.push({ value: s.partnerCode, label: s.name });
    }
    return opts;
  }, [countrySections]);

  // Filter sections by exposure dropdown
  const visibleSections = useMemo(() => {
    if (exposureFilter === 'all') return countrySections;
    return countrySections.filter(s => s.partnerCode === exposureFilter);
  }, [countrySections, exposureFilter]);

  const visibleRows = useMemo(() => {
    return visibleSections.flatMap(s => s.rows);
  }, [visibleSections]);

  // Compute exposure-aware stat cards
  const exposureStats = useMemo(() => {
    const critical = visibleRows.filter(r => r.risk_tier === 'CRITICAL').length;
    const high = visibleRows.filter(r => r.risk_tier === 'HIGH').length;
    const medium = visibleRows.filter(r => r.risk_tier === 'MEDIUM').length;
    const low = visibleRows.filter(r => r.risk_tier === 'LOW').length;
    return { CRITICAL: critical, HIGH: high, MEDIUM: medium, LOW: low };
  }, [visibleRows]);

  const selectedExposureName = exposureFilter === 'all' ? ''
    : countrySections.find(s => s.partnerCode === exposureFilter)?.name || '';

  const heroAlertColor = exposureFilter === '699' ? '#D97706'
    : exposureFilter === '842' ? '#3B82F6'
    : '#DC2626';

  /* -- Sort helpers -- */
  const getNumericField = (row: RiskRow, field: string): number => {
    switch (field) {
      case 'china_pct': return row.china_pct;
      case 'india_pct': return row.india_pct;
      case 'us_pct': return row.us_pct || 0;
      case 'china_yoy_latest': return row.china_yoy_latest;
      case 'china_trend_precovid': return row.china_trend_precovid;
      default: return 0;
    }
  };

  const sortRows = (rows: RiskRow[]) => [...rows].sort((a, b) => {
    if (sortField === 'hs4_code') {
      const cmp = a.hs4_code.localeCompare(b.hs4_code);
      return sortDir === 'desc' ? -cmp : cmp;
    }
    if (sortField === 'hs4_name') {
      const cmp = (a.hs4_name || '').localeCompare(b.hs4_name || '');
      return sortDir === 'desc' ? -cmp : cmp;
    }
    if (sortField === 'risk_tier') {
      const tierOrder: Record<string, number> = { CRITICAL: 4, HIGH: 3, MEDIUM: 2, LOW: 1 };
      const aOrd = tierOrder[a.risk_tier] || 0;
      const bOrd = tierOrder[b.risk_tier] || 0;
      return sortDir === 'desc' ? bOrd - aOrd : aOrd - bOrd;
    }
    if (sortField === 'pattern_type') {
      const cmp = (a.pattern_type || '').localeCompare(b.pattern_type || '');
      return sortDir === 'desc' ? -cmp : cmp;
    }
    if (sortField === 'dominant_pct') {
      const aDom = a.country_breakdown?.[0]?.pct ?? a.china_pct;
      const bDom = b.country_breakdown?.[0]?.pct ?? b.china_pct;
      return sortDir === 'desc' ? bDom - aDom : aDom - bDom;
    }
    const aNum = getNumericField(a, sortField);
    const bNum = getNumericField(b, sortField);
    return sortDir === 'desc' ? bNum - aNum : aNum - bNum;
  });

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const sortArrow = (field: string) => sortField === field ? (sortDir === 'desc' ? ' \u25bc' : ' \u25b2') : ' \u21c5';

  const stats = data?.stat_summary || { CRITICAL: 0, HIGH: 0, MEDIUM: 0, LOW: 0 };

  const toggleSection = (partnerCode: string) => {
    setExpandedSections(prev => ({ ...prev, [partnerCode]: !prev[partnerCode] }));
  };

  /* -- Rendering helpers -- */
  const renderPctBar = (pct: number, color: string) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ width: 72, height: 6, borderRadius: 3, background: T.border, overflow: 'hidden' }}>
        <div style={{
          height: '100%', borderRadius: 3,
          width: `${Math.min(100, Math.round(pct))}%`,
          background: color,
        }} />
      </div>
      <span style={{ fontWeight: 700, color, fontSize: 12 }}>
        {Math.round(pct * 10) / 10}%
      </span>
    </div>
  );

  const getCountryColor = (code: string): string => COUNTRY_COLORS[code] || DEFAULT_COUNTRY_COLOR;

  const renderCountrySection = (section: CountrySection) => {
    const isExpanded = !!expandedSections[section.partnerCode];
    const sorted = sortRows(section.rows);

    // Build columns dynamically per section
    const columns: Array<{ field: string; label: string; align: 'left' | 'center' }> = [
      { field: 'hs4_code', label: 'HS4 Code', align: 'left' },
      { field: 'hs4_name', label: 'HS4 Name', align: 'left' },
      { field: 'dominant_pct', label: `${section.name} %`, align: 'left' },
    ];
    // Show China % column unless this IS the China section
    if (section.partnerCode !== '156') {
      columns.push({ field: 'china_pct', label: 'China %', align: 'left' });
    }
    // Show India % column unless this IS the India section
    if (section.partnerCode !== '699') {
      columns.push({ field: 'india_pct', label: 'India %', align: 'left' });
    }
    columns.push({ field: 'china_yoy_latest', label: 'YoY Trend', align: 'center' });
    columns.push({ field: 'risk_tier', label: 'Risk Tier', align: 'center' });
    columns.push({ field: 'pattern_type', label: 'Pattern', align: 'left' });

    // Get the dominant country % for a row within this section
    const getDominantPct = (row: RiskRow): number => {
      if (section.partnerCode === '156') return row.china_pct;
      if (section.partnerCode === '699') return row.india_pct;
      if (section.partnerCode === '842') return row.us_pct || 0;
      const entry = row.country_breakdown?.find((e: CountryBreakdownEntry) => e.partner_code === section.partnerCode);
      return entry?.pct ?? 0;
    };

    return (
      <div key={section.partnerCode} style={{
        background: T.white, border: `1px solid ${T.border}`,
        borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        overflow: 'hidden',
      }}>
        <div
          onClick={() => toggleSection(section.partnerCode)}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '18px 24px', cursor: 'pointer',
            borderBottom: isExpanded ? `1px solid ${T.border}` : 'none',
            transition: 'background 0.15s',
          }}
          onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = T.offWhite; }}
          onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = ''; }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 10, height: 10, borderRadius: '50%', background: section.color }} />
            <h2 style={{ fontSize: 15, fontWeight: 700, color: T.textDark, margin: 0 }}>
              HS Code Breakdown by {section.name} Exposure
            </h2>
            <span style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              minWidth: 28, padding: '2px 10px', borderRadius: 12,
              fontSize: 11, fontWeight: 700, color: T.white, background: section.color,
            }}>
              {section.rows.length}
            </span>
          </div>
          <span style={{ fontSize: 16, color: T.textLight, transition: 'transform 0.2s', transform: isExpanded ? 'rotate(180deg)' : 'rotate(0)' }}>
            {"\u25bc"}
          </span>
        </div>

        {isExpanded && (
          <div style={{ padding: '0 24px 24px' }}>
            {section.rows.length === 0 ? (
              <div style={{ padding: '32px 0', textAlign: 'center', color: T.textLight, fontSize: 14 }}>
                No HS codes in this category for the selected filters.
              </div>
            ) : (
              <div style={{ overflowX: 'auto', marginTop: 16 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr>
                      {columns.map(col => (
                        <th
                          key={col.field}
                          onClick={() => handleSort(col.field)}
                          style={{
                            padding: '10px 12px', fontSize: 11, fontWeight: 700,
                            textTransform: 'uppercase', letterSpacing: '0.06em',
                            color: sortField === col.field ? T.navy : T.textLight,
                            borderBottom: `2px solid ${sortField === col.field ? T.red : T.border}`,
                            whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none',
                            textAlign: col.align,
                          }}
                        >
                          {col.label}{sortArrow(col.field)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sorted.map(row => (
                      <tr
                        key={`${row.hs4_code}-${row.reporter_code}`}
                        onClick={() => { setSelectedRow(row); setDrawerTab('summary'); }}
                        style={{ borderBottom: `1px solid ${T.borderLight || '#F1F5F9'}`, cursor: 'pointer' }}
                        onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = T.offWhite; }}
                        onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = ''; }}
                      >
                        <td style={{ padding: '13px 12px' }}>
                          <span style={{
                            fontFamily: "'JetBrains Mono', monospace", fontSize: 12,
                            background: tierBg(row.risk_tier), color: tierColor(row.risk_tier),
                            padding: '2px 10px', borderRadius: 4, fontWeight: 700,
                          }}>
                            {row.hs4_code}
                          </span>
                        </td>
                        <td style={{ padding: '13px 12px', fontSize: 12, color: T.textMid, maxWidth: 200 }}>
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
                            {row.hs4_name || '\u2014'}
                          </span>
                        </td>
                        <td style={{ padding: '13px 12px' }}>
                          {renderPctBar(getDominantPct(row), section.color)}
                        </td>
                        {section.partnerCode !== '156' && (
                          <td style={{ padding: '13px 12px' }}>
                            {renderPctBar(row.china_pct, '#DC2626')}
                          </td>
                        )}
                        {section.partnerCode !== '699' && (
                          <td style={{ padding: '13px 12px' }}>
                            {renderPctBar(row.india_pct, '#D97706')}
                          </td>
                        )}
                        <td style={{ padding: '13px 12px', textAlign: 'center' }}>
                          <span style={{
                            fontWeight: 700, fontSize: 12,
                            color: row.china_yoy_latest > 0 ? '#DC2626' : row.china_yoy_latest < 0 ? '#059669' : T.textMid,
                          }}>
                            {row.china_yoy_latest > 0 ? '\u25b2' : row.china_yoy_latest < 0 ? '\u25bc' : '\u2014'}{' '}
                            {row.china_yoy_latest > 0 ? '+' : ''}{Math.round(row.china_yoy_latest * 10) / 10}pp
                          </span>
                        </td>
                        <td style={{ padding: '13px 12px', textAlign: 'center' }}>
                          <span style={{
                            display: 'inline-block', padding: '3px 10px', borderRadius: 12,
                            fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em',
                            background: tierBg(row.risk_tier), color: tierColor(row.risk_tier),
                            border: `1px solid ${tierBorder(row.risk_tier)}`,
                          }}>
                            {row.risk_tier}
                          </span>
                        </td>
                        <td style={{ padding: '13px 12px', fontSize: 12, color: T.textMid }}>
                          {PATTERN_LABELS[row.pattern_type] || row.pattern_type || '\u2014'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {section.rows.length > 0 && (
              <div style={{ marginTop: 12, fontSize: 12, color: T.textLight }}>
                Showing {section.rows.length} HS codes {'\u00b7'} {reporterName}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* Hero */}
      <div style={{
        position: 'relative',
        background: T.offWhite, border: `1px solid ${T.border}`,
        borderRadius: 16,
        padding: '40px 44px',
        overflow: 'hidden',
      }}>
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.red} strokeWidth="2.5">
              <path d="M3 3h7v7H3z"/><path d="M14 3h7v7h-7z"/><path d="M14 14h7v7h-7z"/><path d="M3 14h7v7H3z"/>
            </svg>
            <span style={{ fontSize: 11, fontWeight: 700, color: T.red, textTransform: 'uppercase', letterSpacing: '0.15em', fontFamily: "'IBM Plex Mono', 'Courier New', monospace" }}>
              Comtrade Import Intelligence
            </span>
          </div>
          <h1 style={{ fontSize: 30, fontWeight: 800, color: T.textDark, letterSpacing: '-0.02em', lineHeight: 1.2, marginBottom: 8, fontFamily: "'Playfair Display', Georgia, serif" }}>
            API Import/Export Intelligence
          </h1>
          <p style={{ fontSize: 14, color: T.textMid, maxWidth: 580, lineHeight: 1.6 }}>
            Identify upstream API concentration and hidden supply chain exposure at the country level.
          </p>

          {(exposureStats.CRITICAL > 0 || exposureStats.HIGH > 0) && (
            <div style={{
              marginTop: 20, padding: '16px 20px',
              background: `${heroAlertColor}1E`,
              border: `1px solid ${heroAlertColor}40`,
              borderLeft: `3px solid ${heroAlertColor}`,
              borderRadius: 8, maxWidth: 640,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                  padding: '3px 10px', borderRadius: 20,
                  background: `${heroAlertColor}33`, border: `1px solid ${heroAlertColor}59`,
                  fontSize: 10, fontWeight: 800,
                  color: heroAlertColor === '#D97706' ? '#F59E0B' : heroAlertColor === '#3B82F6' ? '#60A5FA' : '#F87171',
                  letterSpacing: '0.08em', textTransform: 'uppercase',
                }}>
                  HIGH API SUPPLY CHAIN RISK
                </span>
                <span style={{ fontSize: 11, color: T.textMid, fontWeight: 600 }}>
                  {reporterName}{selectedExposureName ? ` \u00b7 ${selectedExposureName}` : ''}{hsChapter ? ` \u00b7 CH ${hsChapter}` : ''}
                </span>
              </div>
              <div style={{ fontSize: 13, color: T.textDark, fontWeight: 600 }}>
                {exposureStats.CRITICAL} critical and {exposureStats.HIGH} high-risk HS codes identified{selectedExposureName ? ` for ${selectedExposureName} exposure` : ''}
              </div>
            </div>
          )}
        </div>
      </div>

      {globalFilters.country.length > 1 && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '10px 16px', borderRadius: 8,
          background: '#EFF6FF', border: '1px solid #BFDBFE',
          fontSize: 13, color: '#1E40AF', fontWeight: 500,
        }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#3B82F6" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/>
          </svg>
          Showing data for <strong>{REPORTERS.find(r => r.code === reporterCode)?.name || reporterCode}</strong>. API Intelligence uses a single reporter country.
        </div>
      )}

      {/* Filter Bar */}
      <div style={{
        display: 'flex', gap: 12, alignItems: 'center',
        background: T.white, border: `1px solid ${T.border}`,
        borderRadius: 12, padding: '14px 20px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        flexWrap: 'wrap',
      }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Importer</span>
        <select
          value={reporterCode}
          onChange={e => setReporterCode(e.target.value)}
          style={{
            padding: '7px 12px', borderRadius: 8,
            border: `1px solid ${T.border}`,
            fontFamily: 'inherit', fontSize: 13, color: T.textDark,
            background: T.offWhite, cursor: 'pointer', outline: 'none',
          }}
        >
          {REPORTERS.map(r => (
            <option key={r.code} value={r.code}>{r.name}</option>
          ))}
        </select>

        <div style={{ width: 1, height: 28, background: T.border }} />

        <span style={{ fontSize: 11, fontWeight: 700, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Exposure Country</span>
        <select
          value={exposureFilter}
          onChange={e => {
            const val = e.target.value;
            setExposureFilter(val);
            if (val === 'all') {
              // Reset to default: expand first section only
              const newExp: Record<string, boolean> = {};
              countrySections.forEach((s, i) => { newExp[s.partnerCode] = i === 0; });
              setExpandedSections(newExp);
            } else {
              // Expand only the selected country section
              const newExp: Record<string, boolean> = {};
              countrySections.forEach(s => { newExp[s.partnerCode] = s.partnerCode === val; });
              setExpandedSections(newExp);
            }
          }}
          style={{
            padding: '7px 12px', borderRadius: 8,
            border: `1px solid ${T.border}`,
            fontFamily: 'inherit', fontSize: 13, color: T.textDark,
            background: T.offWhite, cursor: 'pointer', outline: 'none',
            minWidth: 160,
          }}
        >
          {exposureCountryOptions.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>

        <div style={{ width: 1, height: 28, background: T.border }} />

        <span style={{ fontSize: 11, fontWeight: 700, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.06em' }}>HS Chapter</span>
        <div style={{ display: 'flex', gap: 6 }}>
          {[
            { value: '', label: 'Both' },
            { value: '29', label: 'CH 29 \u2014 APIs' },
            { value: '30', label: 'CH 30 \u2014 Pharma' },
          ].map(ch => (
            <button
              key={ch.value}
              onClick={() => setHsChapter(ch.value)}
              style={{
                padding: '7px 16px', borderRadius: 20,
                border: `1px solid ${hsChapter === ch.value ? T.navy : T.border}`,
                fontSize: 12, fontWeight: 600,
                color: hsChapter === ch.value ? 'white' : T.textMid,
                background: hsChapter === ch.value ? T.navy : T.white,
                cursor: 'pointer', fontFamily: 'inherit',
              }}
            >
              {ch.label}
            </button>
          ))}
        </div>

        <div style={{ width: 1, height: 28, background: T.border }} />

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap' }}>Risk Tier</span>
          <select
            value={riskTierFilter}
            onChange={e => setRiskTierFilter(e.target.value)}
            style={{
              padding: '7px 12px', borderRadius: 8,
              border: `1px solid ${T.border}`,
              fontFamily: 'inherit', fontSize: 13, color: T.textDark,
              background: T.offWhite, cursor: 'pointer', outline: 'none',
            }}
          >
            <option value="">All Tiers</option>
            <option value="CRITICAL">Critical</option>
            <option value="HIGH">High</option>
            <option value="MEDIUM">Medium</option>
            <option value="LOW">Low</option>
          </select>
        </div>

        <div style={{ flex: 1 }} />
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 5,
          padding: '5px 12px', background: 'rgba(12,35,64,0.07)',
          border: '1px solid rgba(12,35,64,0.15)', borderRadius: 20,
          fontSize: 12, fontWeight: 600, color: T.navy,
        }}>
          {data?.total || 0} HS codes analyzed
        </div>
      </div>

      {/* Stat Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
        {([
          { tier: 'CRITICAL', color: '#DC2626', label: 'Critical Risk' },
          { tier: 'HIGH', color: '#DC2626', label: 'High Risk' },
          { tier: 'MEDIUM', color: '#D97706', label: 'Medium Risk' },
          { tier: 'LOW', color: '#059669', label: 'Low Risk' },
        ] as const).map(s => (
          <div key={s.tier} style={{
            background: T.white, border: `1px solid ${T.border}`,
            borderRadius: 12, padding: '20px 22px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
            borderTop: `3px solid ${s.color}`,
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
              {s.label}
            </div>
            <div style={{ fontSize: 28, fontWeight: 800, color: T.textDark, lineHeight: 1, letterSpacing: '-0.02em', marginBottom: 4 }}>
              {exposureFilter === 'all' ? stats[s.tier] : exposureStats[s.tier]}
            </div>
            <div style={{ fontSize: 12, color: T.textMid }}>
              HS codes{selectedExposureName ? ` \u00b7 ${selectedExposureName}` : ''}
            </div>
          </div>
        ))}
      </div>

      {loading && (
        <div style={{
          background: T.white, border: `1px solid ${T.border}`,
          borderRadius: 12, padding: 48, textAlign: 'center',
          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        }}>
          <p style={{ fontSize: 14, color: T.textMid }}>Loading API intelligence data...</p>
        </div>
      )}

      {!loading && visibleSections.map(section => renderCountrySection(section))}

      {!loading && (data?.rows || []).length === 0 && (
        <div style={{
          background: T.white, border: `1px solid ${T.border}`,
          borderRadius: 12, padding: 48, textAlign: 'center',
          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        }}>
          <p style={{ fontSize: 14, color: T.textMid }}>
            No risk data found for the selected filters. Try adjusting the reporter or chapter filter.
          </p>
        </div>
      )}

      {/* Drill-Down Drawer */}
      {selectedRow && (
        <div style={{
          position: 'fixed', top: 0, right: 0, bottom: 0,
          width: 520, maxWidth: '100vw',
          background: T.white, boxShadow: '-4px 0 24px rgba(0,0,0,0.12)',
          zIndex: 1000, display: 'flex', flexDirection: 'column',
          borderLeft: `1px solid ${T.border}`,
        }}>
          <div style={{
            padding: '20px 24px', borderBottom: `1px solid ${T.border}`,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                <span style={{
                  fontFamily: "'JetBrains Mono', monospace", fontSize: 14, fontWeight: 700,
                  background: tierBg(selectedRow.risk_tier), color: tierColor(selectedRow.risk_tier),
                  padding: '2px 12px', borderRadius: 6,
                }}>
                  HS {selectedRow.hs4_code}
                </span>
                <span style={{
                  display: 'inline-block', padding: '3px 10px', borderRadius: 12,
                  fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
                  background: tierBg(selectedRow.risk_tier), color: tierColor(selectedRow.risk_tier),
                  border: `1px solid ${tierBorder(selectedRow.risk_tier)}`,
                }}>
                  {selectedRow.risk_tier}
                </span>
              </div>
              {selectedRow.hs4_name && (
                <div style={{ fontSize: 13, fontWeight: 600, color: T.textDark, marginBottom: 2 }}>
                  {selectedRow.hs4_name}
                </div>
              )}
              <div style={{ fontSize: 12, color: T.textMid }}>
                {reporterName}
                {(selectedRow.country_breakdown && selectedRow.country_breakdown.length > 0
                  ? selectedRow.country_breakdown.slice(0, 4)
                  : [
                      { name: 'China', pct: selectedRow.china_pct, partner_code: '156' },
                      { name: 'India', pct: selectedRow.india_pct, partner_code: '699' },
                    ]
                ).map((entry: CountryBreakdownEntry) => (
                  <span key={entry.partner_code}> {'\u00b7'} {entry.name} {Math.round(entry.pct)}%</span>
                ))}
              </div>
            </div>
            <button
              onClick={() => setSelectedRow(null)}
              style={{
                width: 32, height: 32, borderRadius: 8, border: `1px solid ${T.border}`,
                background: T.offWhite, cursor: 'pointer', display: 'flex',
                alignItems: 'center', justifyContent: 'center', fontSize: 16, color: T.textMid,
                fontFamily: 'inherit',
              }}
            >
              {'\u00d7'}
            </button>
          </div>

          <div style={{ display: 'flex', borderBottom: `1px solid ${T.border}`, padding: '0 24px' }}>
            {([
              { key: 'summary' as const, label: 'Summary' },
              { key: 'short_term' as const, label: 'Short-term' },
              { key: 'long_term' as const, label: 'Long-term' },
              { key: 'full' as const, label: 'Full Analysis' },
            ]).map(tab => (
              <button
                key={tab.key}
                onClick={() => setDrawerTab(tab.key)}
                style={{
                  padding: '12px 16px', border: 'none',
                  borderBottom: drawerTab === tab.key ? `2px solid ${T.navy}` : '2px solid transparent',
                  background: 'none', fontSize: 13,
                  fontWeight: drawerTab === tab.key ? 700 : 500,
                  color: drawerTab === tab.key ? T.navy : T.textMid,
                  cursor: 'pointer', fontFamily: 'inherit',
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div style={{ flex: 1, overflow: 'auto', padding: 24 }}>
            {/* Country breakdown bars in drawer */}
            {selectedRow.country_breakdown && selectedRow.country_breakdown.length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: T.textDark, marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  Source Countries
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {selectedRow.country_breakdown.map((entry: CountryBreakdownEntry, i: number) => {
                    const barColor = getCountryColor(entry.partner_code);
                    return (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ fontSize: 12, color: T.textMid, minWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {entry.name}
                        </span>
                        <div style={{ flex: 1, height: 8, borderRadius: 4, background: T.offWhite, overflow: 'hidden' }}>
                          <div style={{
                            height: '100%', borderRadius: 4,
                            width: `${Math.min(100, entry.pct)}%`,
                            background: barColor,
                          }} />
                        </div>
                        <span style={{ fontSize: 12, fontWeight: 700, color: barColor, minWidth: 42, textAlign: 'right' }}>
                          {entry.pct}%
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '5px 12px', borderRadius: 20,
              background: 'rgba(12,35,64,0.07)', border: '1px solid rgba(12,35,64,0.15)',
              fontSize: 11, fontWeight: 700, color: T.navy, marginBottom: 16,
            }}>
              {PATTERN_LABELS[selectedRow.pattern_type] || selectedRow.pattern_type || 'Unknown'}
            </div>

            {(selectedRow.displacement_flag || selectedRow.acceleration_flag) && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
                {selectedRow.displacement_flag && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderRadius: 8, background: '#FEF2F2', border: '1px solid #FECACA' }}>
                    <span style={{ fontSize: 12, color: '#DC2626', fontWeight: 600 }}>India displacement detected</span>
                  </div>
                )}
                {selectedRow.acceleration_flag && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderRadius: 8, background: '#FEF2F2', border: '1px solid #FECACA' }}>
                    <span style={{ fontSize: 12, color: '#DC2626', fontWeight: 600 }}>Acceleration detected</span>
                  </div>
                )}
              </div>
            )}

            {(() => {
              const bd = selectedRow.country_breakdown || [];
              const topSource = bd.length > 0 ? bd[0] : null;
              const autoSummary = topSource
                ? `${reporterName} sources ${topSource.pct}% of its HS ${selectedRow.hs4_code}${selectedRow.hs4_name ? ` (${selectedRow.hs4_name})` : ''} imports from ${topSource.name}${bd.length > 1 ? `, followed by ${bd.slice(1, 4).map((e: CountryBreakdownEntry) => `${e.name} (${e.pct}%)`).join(', ')}` : ''}. Risk tier: ${selectedRow.risk_tier}.${selectedRow.displacement_flag ? ' India displacement detected.' : ''}${selectedRow.acceleration_flag ? ' Acceleration trend detected.' : ''}`
                : null;
              const noData = <span style={{ color: T.textLight, fontStyle: 'italic' }}>No narrative analysis available for this HS code.</span>;
              return (
                <>
                  {drawerTab === 'summary' && (
                    <div style={{ fontSize: 13, color: T.textMid, lineHeight: 1.7 }}>
                      {selectedRow.narrative_summary || autoSummary || noData}
                    </div>
                  )}
                  {drawerTab === 'short_term' && (
                    <div style={{ fontSize: 13, color: T.textMid, lineHeight: 1.7 }}>
                      {selectedRow.narrative_short_term || noData}
                    </div>
                  )}
                  {drawerTab === 'long_term' && (
                    <div style={{ fontSize: 13, color: T.textMid, lineHeight: 1.7 }}>
                      {selectedRow.narrative_long_term || noData}
                    </div>
                  )}
                  {drawerTab === 'full' && (
                    <div style={{ fontSize: 13, color: T.textMid, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                      {selectedRow.narrative_full || noData}
                    </div>
                  )}
                </>
              );
            })()}

            {selectedRow.series_china && selectedRow.series_china.length > 0 && (
              <div style={{ marginTop: 24 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: T.textDark, marginBottom: 12 }}>
                  China Share by Year
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 80 }}>
                  {selectedRow.series_china.map((s, i) => {
                    const maxPct = Math.max(...selectedRow.series_china.map(x => x.pct || 0), 1);
                    return (
                      <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, flex: 1 }}>
                        <span style={{ fontSize: 9, color: T.textLight }}>{Math.round(s.pct || 0)}%</span>
                        <div style={{
                          width: 16, borderRadius: '3px 3px 0 0',
                          height: Math.max(2, ((s.pct || 0) / maxPct) * 56),
                          background: '#DC2626',
                        }} />
                        <span style={{ fontSize: 10, color: T.textLight }}>{s.year}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {selectedRow && (
        <div
          onClick={() => setSelectedRow(null)}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.3)', zIndex: 999,
          }}
        />
      )}

      <div style={{ fontSize: 11, color: T.textLight, textAlign: 'center', paddingTop: 16, borderTop: `1px solid ${T.borderLight || '#F1F5F9'}` }}>
        Source: UN Comtrade bilateral trade flows {'\u00b7'} HS 2022 nomenclature {'\u00b7'} Values in USD CIF
      </div>
    </div>
  );
}
